
import React, {
  useState,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from "react";
import { List } from "react-window";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

export const ROW_HEIGHT      = 65;
export const MAX_LIST_HEIGHT = 420;

const GRID_COLS =
  "52px 80px minmax(140px,1fr) 140px minmax(160px,1.5fr) minmax(200px,1.5fr)";

const HEADER_LABELS = ["CDE", "Select Column", "Metadata", "Business Description", "Transformation Logic"];


function useContainerWidth(ref) {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const measure = () => {
      const w = ref.current?.getBoundingClientRect().width ?? 0;
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width ?? 0;
        if (w > 0) setWidth(w);
      });
      ro.observe(ref.current);
      return () => ro.disconnect();
    }
  }, []); 
  return width;
}


const nativeCheckboxStyle      = { width: 16, height: 16, cursor: "pointer", accentColor: "#bb1ccc" };
const nativeSwitchContainer    = { position: "relative", display: "inline-block", width: 34, height: 20, flexShrink: 0 };
const nativeInputBase          = { width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
const nativeInputStyle         = { ...nativeInputBase, backgroundColor: "white",   color: "#111827" };
const nativeInputDisabledStyle = { ...nativeInputBase, backgroundColor: "#f9fafb", color: "#9ca3af", cursor: "not-allowed" };
const editBtnBase              = { flexShrink: 0, background: "none", border: "none", padding: "4px", borderRadius: 4, display: "flex", alignItems: "center" };
const editBtnStyle             = { ...editBtnBase, cursor: "pointer",     color: "#bb1ccc" };
const editBtnDisabled          = { ...editBtnBase, cursor: "not-allowed", color: "#9ca3af" };


const NativeSwitch = memo(function NativeSwitch({ checked, onChange, disabled }) {
  return (
    <label style={{ ...nativeSwitchContainer, opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled}
        style={{ opacity: 0, width: 0, height: 0, position: "absolute" }} />
      <span style={{ position: "absolute", inset: 0, backgroundColor: checked ? "#ff9800" : "#d1d5db", borderRadius: 10, transition: "background-color 0.2s" }} />
      <span style={{ position: "absolute", top: 2, left: checked ? 16 : 2, width: 16, height: 16, backgroundColor: "white", borderRadius: "50%", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
    </label>
  );
});



const ColumnRow = memo(function ColumnRow({
  index, style,
  columns,
  selectedSet,   
  columnDetails,
  onToggleColumn, onCdeChange, onDescriptionChange,
  onTransformationLogicChange, onOpenDialog,
}) {
  const column     = columns[index];
  const isSelected = selectedSet.has(column.id);  
  const details    = columnDetails[column.id] || {};

  const rowBg = isSelected
    ? index % 2 === 0 ? "#f0f8ff" : "#e8f4ff"
    : index % 2 === 0 ? "#ffffff" : "#fafbfc";

  return (
    <div style={{
      ...style,
      display: "grid", gridTemplateColumns: GRID_COLS,
      alignItems: "center", borderBottom: "1px solid #f3f4f6",
      backgroundColor: rowBg, boxSizing: "border-box", padding: "0 4px",
    }}>

      {/* Row select checkbox */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleColumn(column.id)}
          style={nativeCheckboxStyle}
          aria-label={`select column ${column.name}`}
        />
      </div>

      {/* CDE switch */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <NativeSwitch
          checked={details.cde || false}
          onChange={(e) => onCdeChange(column.id, e.target.checked)}
          disabled={!isSelected}
        />
      </div>

      {/* Column name + type */}
      <div style={{ padding: "0 8px", overflow: "hidden" }}>
        <span title={column.name} style={{ fontWeight: 500, fontSize: 13, color: "#111827", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {column.name}
        </span>
        <span style={{ fontSize: 11, color: "#6b7280" }}>({column.type})</span>
      </div>

      {/* Metadata */}
      <div style={{ padding: "0 8px", fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={column.metadata}>
        {column.metadata}
      </div>

      {/* Business Description */}
      <div style={{ padding: "0 8px" }}>
        <input
          type="text"
          placeholder="Enter business description..."
          value={details.description || ""}
          onChange={(e) => onDescriptionChange(column.id, e.target.value)}
          disabled={!isSelected}
          style={isSelected ? nativeInputStyle : nativeInputDisabledStyle}
          onFocus={(e) => { if (isSelected) e.target.style.borderColor = "#bb1ccc"; }}
          onBlur={(e)  => { e.target.style.borderColor = "#d1d5db"; }}
        />
      </div>

      {/* Transformation Logic */}
      <div style={{ padding: "0 8px", display: "flex", alignItems: "center", gap: 4 }}>
        <input
          type="text"
          placeholder="Enter transformation logic..."
          value={details.transformationLogic || ""}
          onChange={(e) => onTransformationLogicChange(column.id, e.target.value)}
          disabled={!isSelected}
          style={isSelected ? { ...nativeInputStyle, flex: 1 } : { ...nativeInputDisabledStyle, flex: 1 }}
          onFocus={(e) => { if (isSelected) e.target.style.borderColor = "#bb1ccc"; }}
          onBlur={(e)  => { e.target.style.borderColor = "#d1d5db"; }}
        />
        <button onClick={() => onOpenDialog(column.id)} disabled={!isSelected} title="Open full editor"
          style={isSelected ? editBtnStyle : editBtnDisabled}>
          <EditIcon style={{ fontSize: 16 }} />
        </button>
      </div>
    </div>
  );
});

// ─── ColumnSelector ───────────────────────────────────────────────────────────

const ColumnSelector = ({
  columns,          
  selectedColumns,  
  onToggleColumn,
  onSelectAll,
  columnDetails,
  onCdeChange,
  onDescriptionChange,
  onMetadataChange,
  onTransformationLogicChange,
}) => {
  
  const containerRef = useRef(null);
  const width        = useContainerWidth(containerRef);

  const [openDialog, setOpenDialog]           = useState(false);
  const [currentColumnId, setCurrentColumnId] = useState(null);
  const [tempTL, setTempTL]                   = useState("");


  const selectedSet = useMemo(() => new Set(selectedColumns), [selectedColumns]);


  const visibleSelectedCount = useMemo(
    () => columns.filter((col) => selectedSet.has(col.id)).length,
    [columns, selectedSet]
  );
  const allSelected    = columns.length > 0 && visibleSelectedCount === columns.length;
  const isIndeterminate = visibleSelectedCount > 0 && visibleSelectedCount < columns.length;

  // ── Dialog ────────────────────────────────────────────────────────────────

  const handleOpenDialog = useCallback((colId) => {
    setCurrentColumnId(colId);
    setTempTL(columnDetails[colId]?.transformationLogic || "");
    setOpenDialog(true);
  }, [columnDetails]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setCurrentColumnId(null);
    setTempTL("");
  }, []);

  const handleSave = useCallback(() => {
    if (currentColumnId !== null) onTransformationLogicChange(currentColumnId, tempTL);
    handleCloseDialog();
  }, [currentColumnId, tempTL, onTransformationLogicChange, handleCloseDialog]);

 
  const rowProps = useMemo(() => ({
    columns,
    selectedSet,      
    columnDetails,
    onToggleColumn,
    onCdeChange,
    onDescriptionChange,
    onTransformationLogicChange,
    onOpenDialog: handleOpenDialog,
  }), [
    columns, selectedSet, columnDetails,
    onToggleColumn, onCdeChange, onDescriptionChange,
    onTransformationLogicChange, handleOpenDialog,
  ]);

  const listHeight = Math.min(columns.length * ROW_HEIGHT, MAX_LIST_HEIGHT);

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", backgroundColor: "#fff" }}>

      {/* Sticky header */}
      <div style={{
        display: "grid", gridTemplateColumns: GRID_COLS,
        backgroundColor: "#f8fafc", borderBottom: "2px solid #e5e7eb",
        alignItems: "center", padding: "0 4px",
        position: "sticky", top: 0, zIndex: 1,
      }}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Checkbox
            indeterminate={isIndeterminate}
            checked={allSelected}
            onChange={onSelectAll}
            size="small"
            inputProps={{ "aria-label": "select all columns" }}
            sx={{
              color: "#ff32a0",
              "&.Mui-checked": { color: "#ff32a0" },
              "&.MuiCheckbox-indeterminate": { color: "#ff32a0" },
            }}
          />
        </div>
        {HEADER_LABELS.map((label) => (
          <div key={label} style={{ padding: "14px 8px", fontWeight: 600, fontSize: 14, color: "#ff32a0", whiteSpace: "nowrap" }}>
            {label}
          </div>
        ))}
      </div>

      {/* Virtualised rows */}
      <div ref={containerRef} style={{ width: "100%" }}>
        {columns.length === 0 ? (
          <div style={{ padding: "32px", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
            No columns match your search.
          </div>
        ) : width > 0 ? (
          <List
            height={listHeight}
            width={width}
            rowCount={columns.length}
            rowHeight={ROW_HEIGHT}
            rowComponent={ColumnRow}
            rowProps={rowProps}
            overscanCount={5}
          />
        ) : null}
      </div>

      {/* Transformation Logic dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#f8fafc", borderBottom: "1px solid #e5e7eb", padding: "16px 24px" }}>
          <span style={{ fontWeight: 600, color: "#bb1ccc", fontSize: "18px" }}>Transformation Logic</span>
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: "#6b7280" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <textarea rows={12} value={tempTL} onChange={(e) => setTempTL(e.target.value)}
            placeholder="Enter transformation logic here..."
            style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid #d1d5db", fontSize: "14px", fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }}
            onFocus={(e) => { e.target.style.borderColor = "#bb1ccc"; e.target.style.borderWidth = "2px"; }}
            onBlur={(e)  => { e.target.style.borderColor = "#d1d5db"; e.target.style.borderWidth = "1px"; }} />
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", borderTop: "1px solid #e5e7eb", backgroundColor: "#f8fafc" }}>
          <Button onClick={handleCloseDialog} sx={{ color: "#6b7280", textTransform: "none", fontWeight: 500 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: "#bb1ccc", color: "#fff", textTransform: "none", fontWeight: 500, borderRadius: "6px", padding: "8px 24px", "&:hover": { backgroundColor: "#000080" } }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ColumnSelector;